import React, { useState, useRef } from 'react';
import { authPost } from '../../lib/authFetch';
import { isLocalModeEnabled, getLocalDonors } from '../../lib/localMode';
import DesktopStrategyField from './DesktopStrategyField';
import DesktopStrategyPath from './DesktopStrategyPath';
import './DesktopStrategyView.css';

const EXAMPLES = [
  'Raise $500,000 for an education gala',
  'We need $250,000 this quarter, lead gifts first',
  'Raise $1M, but the board has to go first'
];

// Owns Strategy Mode's two render states (field / path) and the chat dock that
// switches between them. Nothing here is persisted - every plan is a fresh,
// stateless call to /api/strategy/plan, recomputed from real donor data.
function DesktopStrategyView({ donors }) {
  const [mode, setMode] = useState('field'); // 'field' | 'path'
  const [strategies, setStrategies] = useState([]);
  const [goal, setGoal] = useState(null);
  const [versionIndex, setVersionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const submitBrief = async (brief) => {
    const text = (brief || '').trim();
    if (!text || loading) return;

    setLoading(true);
    setError('');

    try {
      const local = isLocalModeEnabled();
      const response = await authPost('/api/strategy/plan', {
        brief: text,
        ...(local ? { localMode: true, localDonors: getLocalDonors() } : {})
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong building the plan.');
      } else if (!data.strategies || data.strategies.length === 0) {
        setError("Couldn't find a workable path through your current donors for that brief.");
      } else {
        setStrategies(data.strategies);
        setGoal(typeof data.goal === 'number' ? data.goal : null);
        setVersionIndex(0);
        setMode('path');
      }
    } catch (err) {
      console.error('Strategy plan request failed:', err);
      setError('Something went wrong building the plan.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    const value = inputRef.current?.value.trim();
    if (!value) return;
    inputRef.current.value = '';
    submitBrief(value);
  };

  const backToGrid = () => {
    setMode('field');
    setStrategies([]);
    setVersionIndex(0);
    setError('');
  };

  return (
    <div className="strategy-view">
      {mode === 'field' ? (
        <DesktopStrategyField donors={donors} />
      ) : (
        <DesktopStrategyPath
          donors={donors}
          strategies={strategies}
          goal={goal}
          versionIndex={versionIndex}
          onChangeVersion={setVersionIndex}
          onBackToGrid={backToGrid}
        />
      )}

      <div className="strategy-dock">
        {mode === 'field' && (
          <div className="strategy-chips">
            {EXAMPLES.map((example, i) => (
              <button key={i} className="strategy-chip" onClick={() => submitBrief(example)} disabled={loading}>
                {example}
              </button>
            ))}
          </div>
        )}
        <div className="strategy-cbox">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={loading ? 'Charting a path…' : 'Describe your campaign…'}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button className="strategy-send" onClick={handleSend} disabled={loading} aria-label="Send">
            {loading ? <span className="strategy-send-spinner" /> : <span>↑</span>}
          </button>
        </div>
        {error && <p className="strategy-status error">{error}</p>}
      </div>
    </div>
  );
}

export default DesktopStrategyView;
